import React, { useEffect, useRef, useState } from 'react'
import { setAccessToken, clearAccessTokens } from 'api/accessTokenMgmt'
import { rememberAccountReviewAuthHint } from '../../bootstrap/accountReviewSession'
import { Link, useNavigate } from 'react-router-dom'
import HeroImage from 'components/auth/HeroImage.jsx'
import flashStyles from 'components/shared/flash.module.scss'
import styles from 'components/auth/auth.module.scss'
import cn from 'classnames'
import useStore from 'state/knovStore'
import api from 'api/api'
import { navigateAfterAuth } from './postAuthNavigation'
import { Eye, EyeOff } from 'lucide-react'
import { createYoursLoginProof } from 'lib/yours-wallet-auth'
import {
    setFirstTouchAuthSurface,
    getFirstTouchAuthSurface,
    getAuthSurfaceForTracking,
    setSignupMethod,
    QUALIFYING_SURFACES,
} from 'lib/authSurfaceTracking'
import { markHelloPromptPending, markWalletSetupPending } from 'lib/signupOnboarding'
import TurnstileWidget from './TurnstileWidget'
import OAuthButtons from './OAuthButtons'

export const usernameRegex = /^[a-zA-Z0-9_-]+$/

// Matches RegistrationsController::TURNSTILE_ACTION.
const TURNSTILE_ACTION = 'signup'
// A token usually arrives while the form is being filled; a fast submit waits for it.
const TURNSTILE_WAIT_MS = 5000
const TURNSTILE_INCOMPLETE_MESSAGE = 'Please complete the verification and try again.'
const TURNSTILE_UNAVAILABLE_MESSAGE =
    'The verification step could not load. Check your connection or content blocker, then reload the page.'

const SIGNUP_ATTRIBUTION_KEYS = [
    'prospect_token',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'landing_page',
    'landing_button',
    'landing_page_url',
    'landing_page_host',
    'landing_page_path',
    'landing_page_search',
    'landing_referrer',
    'amplitude_device_id',
]

export default function SignUp({
    registration_path = '/auth',
    stream_invite_token = '',
    stream_join = '',
    space_invite_token = '',
    wallet = '',
    oauth_signup_token = '',
    embedded = false,
    analyticsSource = undefined,
    onSwitchMode = undefined,
    onAuthenticated = undefined,
    attribution = {},
}) {
    const navigate = useNavigate()
    const [showPassword, setShowPassword] = useState(false)
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false)
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        honeypot: '',
    })
    const [emailSignup, setEmailSignup] = useState(wallet !== 'yours')
    // Back from Google / X without an existing account: pick a username (and
    // give an email when the provider had none) to finish.
    const [oauthSignup, setOauthSignup] = useState(
        /** @type {{ provider: string, email: string | null, email_required: boolean } | null} */ (
            null
        ),
    )
    const [oauthSignupLoading, setOauthSignupLoading] = useState(Boolean(oauth_signup_token))
    const [turnstileSiteKey, setTurnstileSiteKey] = useState(/** @type {string | null} */ (null))
    const [turnstileUnavailable, setTurnstileUnavailable] = useState(false)
    // New accounts get a wallet made in this browser (LocalWallets::Config).
    // The submit waits for this (briefly) so a fast submit can't miss the setting.
    const signupConfig = useRef(/** @type {Promise<any> | null} */ (null))
    const turnstileRef = useRef(
        /** @type {import('./TurnstileWidget').TurnstileHandle | null} */ (null),
    )
    /** @type {Record<string, string>} */
    const attributionParams = SIGNUP_ATTRIBUTION_KEYS.reduce((params, key) => {
        const value = attribution?.[key]
        if (value) params[key] = value
        return params
    }, /** @type {Record<string, string>} */ ({}))

    useEffect(() => {
        if (attributionParams.amplitude_device_id && window.amplitude) {
            window.amplitude.getInstance().setDeviceId(attributionParams.amplitude_device_id)
        }

        const existingAuthSurface = getFirstTouchAuthSurface()

        if (!existingAuthSurface) {
            let surfaceForStorage
            if (attributionParams.landing_button) {
                surfaceForStorage = QUALIFYING_SURFACES.LANDING_CTA
            } else if (embedded) {
                surfaceForStorage = QUALIFYING_SURFACES.MODAL
            } else {
                surfaceForStorage = QUALIFYING_SURFACES.DIRECT
            }
            setFirstTouchAuthSurface(surfaceForStorage)
        }

        const authSurfaceForEvent = getAuthSurfaceForTracking()

        window.logEv('VIEW_AUTH_SIGNUP', {
            auth_mode: 'signup',
            auth_surface: authSurfaceForEvent,
            signup_method: emailSignup ? 'email' : 'wallet',
            trigger_source: analyticsSource || 'direct_signup',
            ...attributionParams,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // /signup renders without the app bootstrap, so ask for the one key it needs.
    useEffect(() => {
        let cancelled = false
        signupConfig.current = fetch(`${window.knovApiUrl}/api/v1/gon/signup`, {
            headers: { Accept: 'application/json' },
        })
            .then(response => (response.ok ? response.json() : null))
            .then(config => {
                if (!cancelled && config?.TURNSTILE_SITE_KEY) {
                    setTurnstileSiteKey(config.TURNSTILE_SITE_KEY)
                }
                return config
            })
            .catch(error => {
                console.warn('Signup config unavailable:', error)
                return null
            })
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (!oauth_signup_token) return
        let cancelled = false
        fetch(`${window.knovApiUrl}/oauth/signup?token=${encodeURIComponent(oauth_signup_token)}`, {
            headers: { Accept: 'application/json' },
        })
            .then(async response => {
                const data = await response.json().catch(() => null)
                if (cancelled) return
                if (response.ok && data?.provider) {
                    setOauthSignup(data)
                    setFormData(current => ({
                        ...current,
                        name: data.suggested_name || '',
                        email: data.email || '',
                    }))
                } else {
                    setSuccess(false)
                    setMessages([
                        data?.errors?.[0] || 'That sign-up link expired. Please try again.',
                    ])
                }
            })
            .catch(() => {
                if (cancelled) return
                setSuccess(false)
                setMessages(['That sign-up link expired. Please try again.'])
            })
            .finally(() => {
                if (!cancelled) setOauthSignupLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [oauth_signup_token])

    const loginQueryParams = new URLSearchParams()
    if (space_invite_token) loginQueryParams.set('space_invite', space_invite_token)
    if (stream_invite_token) loginQueryParams.set('stream_invite', stream_invite_token)
    Object.entries(attributionParams).forEach(([key, value]) => {
        if (value) loginQueryParams.set(key, value)
    })
    const loginQuery = loginQueryParams.toString() ? `?${loginQueryParams.toString()}` : ''

    const handleChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value })
    }

    const [success, setSuccess] = useState(/** @type {boolean | undefined} */ (undefined))
    const [messages, setMessages] = useState(/** @type {string[]} */ ([]))

    const handleSubmit = async (e, walletProof = null) => {
        const bsvPubkey = walletProof?.pubkey || null
        clearAccessTokens()
        try {
            e.preventDefault()

            const authSurfaceForEvent = getAuthSurfaceForTracking()
            const signupMethod = oauthSignup ? oauthSignup.provider : bsvPubkey ? 'wallet' : 'email'

            window.logEv('CLICK_AUTH_SIGNUP_SUBMIT', {
                auth_mode: 'signup',
                auth_surface: authSurfaceForEvent,
                signup_method: signupMethod,
                trigger_source: analyticsSource || 'direct_signup',
                ...attributionParams,
            })

            // validate username
            if (!usernameRegex.test(formData?.name)) {
                setSuccess(false)
                setMessages([
                    'Username can only contain letters, numbers, underscores, and dashes.',
                ])
                return
            }

            let turnstileToken = null
            if (turnstileSiteKey && !oauthSignup) {
                if (!turnstileUnavailable) {
                    turnstileToken = await turnstileRef.current?.getToken(TURNSTILE_WAIT_MS)
                }
                if (!turnstileToken) {
                    setSuccess(false)
                    setMessages([
                        turnstileUnavailable
                            ? TURNSTILE_UNAVAILABLE_MESSAGE
                            : TURNSTILE_INCOMPLETE_MESSAGE,
                    ])
                    return
                }
            }

            const config = await Promise.race([
                signupConfig.current,
                new Promise(resolve => setTimeout(() => resolve(null), 5000)),
            ])
            const localWalletSignup = config?.LOCAL_WALLET_SIGNUP === true

            /** @type {Record<string, string | null>} */
            const params = {
                name: formData?.name,
                email: formData?.email,
                ...(oauthSignup
                    ? { oauth_signup: oauth_signup_token }
                    : {
                          password: formData?.password,
                          password_confirmation: formData?.password,
                      }),
                // need to pass it in directly rather than reading from state or we can miss it due to race condition
                ...(bsvPubkey && {
                    bsv_pubkey: bsvPubkey,
                    sig: walletProof.sig,
                    msg: walletProof.msg,
                    signature_scheme: walletProof.signature_scheme,
                }),
                ...(localWalletSignup && !bsvPubkey && { local_wallet: 'true' }),
                signup_method: signupMethod,
                auth_surface: authSurfaceForEvent,
                ...(turnstileToken && { 'cf-turnstile-response': turnstileToken }),
                ...attributionParams,
            }

            if (space_invite_token) {
                params.space_invite = space_invite_token
            }
            if (stream_invite_token) {
                params.stream_invite = stream_invite_token
            }
            if (stream_join) {
                params.stream_join = stream_join
            }

            const response = await fetch(`${window.knovApiUrl}${registration_path}`, {
                method: 'POST',
                headers: {
                    'Content-type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(params),
            })

            const data = await response.json()
            if (response.ok) {
                const token = {
                    'access-token': response.headers.get('access-token'),
                    expiry: response.headers.get('expiry'),
                    uid: response.headers.get('uid'),
                    client: response.headers.get('client'),
                }

                //console.log('Retrieved token val: ', token)
                setAccessToken(token)
                rememberAccountReviewAuthHint(window, token, data?.data)

                setSignupMethod(signupMethod)
                // Browser-wallet signups see the wallet setup first; it asks for the
                // hello post when it closes.
                if (localWalletSignup && !bsvPubkey) markWalletSetupPending(data?.data?.id)
                else markHelloPromptPending(data?.data?.id)

                const continueToHome = () => {
                    if (!embedded) onAuthenticated?.()
                    navigateAfterAuth({
                        embedded,
                        navigate,
                        fallbackPath: data?.path || '/',
                        isNewSignup: true,
                    })
                }

                if (data.email && data.password) {
                    const modalActions = useStore.getState().actions.modalActions
                    modalActions.openConfirmModal(
                        <div>
                            <strong>Welcome to Treechat!</strong>
                            <br />
                            <br />
                            We created a login for you to use on mobile, where Yours wallet is not
                            available.
                            <br />
                            <br />
                            <strong>Email:</strong> {data.email}
                            <br />
                            <strong>Password:</strong> {data.password}
                            <br />
                            <br />
                            Please save these credentials, you'll need them to log in on mobile
                            devices.
                        </div>,
                        'Done Saving',
                        continueToHome,
                        {
                            hideCancel: true,
                            onCancel: continueToHome,
                        },
                    )
                } else {
                    continueToHome()
                }
            } else {
                window.logEv('ERROR_AUTH_SIGNUP', {
                    auth_mode: 'signup',
                    auth_surface: authSurfaceForEvent,
                    signup_method: signupMethod,
                    trigger_source: analyticsSource || 'direct_signup',
                    status: response.status,
                    ...attributionParams,
                })
                // The server spends the token even when the signup fails.
                turnstileRef.current?.reset()
                if (data?.TURNSTILE_SITE_KEY) setTurnstileSiteKey(data.TURNSTILE_SITE_KEY)
                setSuccess(false)
                if (data?.errors?.full_messages?.length > 0) {
                    setMessages(data.errors.full_messages)
                } else {
                    setMessages(['An unexpected error occurred.'])
                }
            }
        } catch (error) {
            console.error('Signup Error:', error)
            turnstileRef.current?.reset()
            const authSurfaceForEvent = getAuthSurfaceForTracking()
            const signupMethod = oauthSignup ? oauthSignup.provider : bsvPubkey ? 'wallet' : 'email'

            window.logEv('ERROR_AUTH_SIGNUP', {
                auth_mode: 'signup',
                auth_surface: authSurfaceForEvent,
                signup_method: signupMethod,
                trigger_source: analyticsSource || 'direct_signup',
                error_name: error?.name,
                ...attributionParams,
            })
            setSuccess(false)
            setMessages(['An error occurred during signup.'])
        }
    }

    const handleSwitchToLogin = event => {
        const authSurfaceForEvent = getAuthSurfaceForTracking()

        window.logEv('CLICK_AUTH_SWITCH_TO_LOGIN', {
            auth_mode: 'signup',
            auth_surface: authSurfaceForEvent,
            trigger_source: analyticsSource || 'direct_signup',
            ...attributionParams,
        })

        if (onSwitchMode) {
            event.preventDefault()
            onSwitchMode()
            return
        }

        navigate(`/login${loginQuery}`)
    }

    const oauthMode = Boolean(oauth_signup_token) && (oauthSignupLoading || Boolean(oauthSignup))
    const oauthProviderLabel = oauthSignup?.provider === 'x' ? 'X' : 'Google'

    const statusStyles = messages?.length > 0 && success ? flashStyles.success : flashStyles.error

    const handlePandaSignup = async event => {
        try {
            // The server needs a fresh signature to prove the wallet owns this key.
            const proof = await createYoursLoginProof(
                () => `Treechat signup: sign up for Treechat (${Math.floor(Date.now() / 1000)})`,
            )
            setFormData(formData => ({ ...formData, bsv_pubkey: proof.pubkey }))
            await handleSubmit(event, proof)
        } catch (error) {
            alert(error instanceof Error ? error.message : 'Yours Wallet connection failed.')
            setEmailSignup(false)
        }
    }

    const formContent = (
        <div
            className={cn(
                'sign-up-form',
                styles.signUpForm,
                styles.authSurface,
                embedded && styles.embeddedForm,
            )}
        >
            <div className={cn(!embedded && 'col-md-offset-2 col-md-8')} id="signup-container">
                <div className={styles.statusContainer}>
                    {messages.map((message, index) => (
                        <div key={index} className={cn(styles.errorMessage, statusStyles)}>
                            {message}
                        </div>
                    ))}
                </div>

                <form
                    onSubmit={event => (emailSignup ? handleSubmit(event) : event.preventDefault())}
                >
                    {oauthMode ? (
                        <div className={cn(styles.fieldContainer, styles.oauthSignupIntro)}>
                            {oauthSignupLoading
                                ? 'Finishing sign-in…'
                                : oauthSignup?.email_required
                                ? `Choose a username. ${oauthProviderLabel} didn't share an email, so add one for account recovery and notifications.`
                                : `Signing up with ${oauthProviderLabel} as ${oauthSignup?.email}. Choose a username to finish.`}
                        </div>
                    ) : (
                        <OAuthButtons
                            mode="signup"
                            embedded={embedded}
                            attribution={{
                                ...attributionParams,
                                space_invite: space_invite_token,
                                stream_invite: stream_invite_token,
                                stream_join,
                            }}
                            onStart={provider =>
                                window.logEv('CLICK_AUTH_OAUTH', {
                                    provider,
                                    auth_mode: 'signup',
                                    auth_surface: getAuthSurfaceForTracking(),
                                    trigger_source: analyticsSource || 'direct_signup',
                                    ...attributionParams,
                                })
                            }
                        />
                    )}
                    <div className={cn('sign-up-fields', styles.authFields)}>
                        <div className={cn('field', styles.fieldContainer)}>
                            <span className={cn('field-label', styles.fieldLabel)}>Username</span>
                            <input
                                style={{
                                    fontSize: /* this prevents autozoom on mobile */ '16px',
                                }}
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') {
                                        if (emailSignup) {
                                            // Allow form submission
                                        } else {
                                            event.preventDefault()
                                            setMessages([
                                                'Please click the Sign Up button to continue with Yours Wallet.',
                                            ])
                                        }
                                    }
                                }}
                                autoFocus
                                placeholder=""
                                className={cn('form-control', styles.inputField)}
                                data-testid="signup-name-input"
                            />
                        </div>

                        {/* The provider's verified email is used as-is; only ask when it had none. */}
                        {(!oauthMode || oauthSignup?.email_required) && (
                            <div className={cn('field', styles.fieldContainer)}>
                                <span className={cn('field-label', styles.fieldLabel)}>Email</span>
                                <input
                                    style={{
                                        fontSize: /* this prevents autozoom on mobile */ '16px',
                                    }}
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder=""
                                    className={cn('form-control', styles.inputField)}
                                    autoComplete="email"
                                    data-testid="signup-email-input"
                                />
                            </div>
                        )}
                        {!oauthMode && (
                            <>
                                <div className={cn('field', styles.fieldContainer)}>
                                    <span className={cn('field-label', styles.fieldLabel)}>
                                        Password
                                    </span>
                                    <div className={styles.passwordField}>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            style={{
                                                fontSize:
                                                    /* this prevents autozoom on mobile */ '16px',
                                            }}
                                            value={formData.password}
                                            onChange={handleChange}
                                            autoComplete="new-password"
                                            className={cn(
                                                'form-control',
                                                styles.inputField,
                                                styles.passwordInput,
                                            )}
                                            placeholder="8 character minimum"
                                            data-testid="signup-password-input"
                                        />
                                        <button
                                            type="button"
                                            className={styles.passwordToggle}
                                            onClick={() =>
                                                setShowPassword(currentValue => !currentValue)
                                            }
                                            aria-label={
                                                showPassword ? 'Hide password' : 'Show password'
                                            }
                                        >
                                            {showPassword ? (
                                                <EyeOff size={16} />
                                            ) : (
                                                <Eye size={16} />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className={cn('field', styles.fieldContainer)}>
                                    <span className={cn('field-label', styles.fieldLabel)}>
                                        Confirm Password
                                    </span>
                                    <div className={styles.passwordField}>
                                        <input
                                            type={showPasswordConfirmation ? 'text' : 'password'}
                                            name="password_confirmation"
                                            value={formData.password_confirmation}
                                            onChange={handleChange}
                                            autoComplete="new-password"
                                            className={cn(
                                                'form-control',
                                                styles.inputField,
                                                styles.passwordInput,
                                            )}
                                            placeholder=""
                                            data-testid="signup-password-confirm-input"
                                        />
                                        <button
                                            type="button"
                                            className={styles.passwordToggle}
                                            onClick={() =>
                                                setShowPasswordConfirmation(
                                                    currentValue => !currentValue,
                                                )
                                            }
                                            aria-label={
                                                showPasswordConfirmation
                                                    ? 'Hide confirm password'
                                                    : 'Show confirm password'
                                            }
                                        >
                                            {showPasswordConfirmation ? (
                                                <EyeOff size={16} />
                                            ) : (
                                                <Eye size={16} />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="field" style={{ display: 'none' }}>
                            <input
                                type="text"
                                name="honeypot"
                                value={formData.honeypot}
                                onChange={handleChange}
                                autoComplete="off"
                                className={cn('form-control', styles.inputField)}
                                placeholder="honeypot"
                            />
                        </div>
                        {turnstileSiteKey && !oauthMode && (
                            <TurnstileWidget
                                ref={turnstileRef}
                                siteKey={turnstileSiteKey}
                                action={TURNSTILE_ACTION}
                                onLoadError={() => {
                                    setTurnstileUnavailable(true)
                                    setSuccess(false)
                                    setMessages([TURNSTILE_UNAVAILABLE_MESSAGE])
                                }}
                            />
                        )}
                    </div>

                    <div className={styles.signupContainer}>
                        <button
                            type="submit"
                            className={cn(styles.loginButton)}
                            data-testid="signup-submit-button"
                            disabled={oauthSignupLoading}
                        >
                            Sign up
                        </button>
                    </div>
                </form>

                <div className={cn('auth-links', styles.loginContainer)}>
                    <div className={cn(styles.loginComp, styles.agreementLine)}>
                        By signing up you agree to our{' '}
                        <a
                            className={styles.subtleLink}
                            href="https://treechat.com/tos"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="signup-tos-link"
                        >
                            Terms of Service
                        </a>
                    </div>
                </div>

                <div className={cn('auth-links', styles.loginContainer)}>
                    <div className={styles.loginComp}>
                        {'Already have an account?'}

                        <Link
                            className={cn(
                                'devise-shared-signup',
                                styles.linkHover,
                                styles.textLink,
                            )}
                            to={`/login${loginQuery}`}
                            data-testid="signup-login-link"
                            onClick={handleSwitchToLogin}
                        >
                            {' '}
                            Login{' '}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )

    if (embedded) {
        return formContent
    }

    return (
        <div className="registrations_new landing">
            <div className="earth container-fluid">
                <div className="row">
                    <HeroImage />
                </div>
                {formContent}
            </div>
        </div>
    )
}
